import React, { useMemo, useState } from 'react';
import useWindowSize from '../../../../shared/hooks/use-window-size';
import { filterNonEmptyValues } from '../../../../shared/utils/object-utils';
import { isCoinsEquals } from '../../../currency/currency-service';
import ClaimIro from '../../../iro/claim-iro/claim-iro';
import Swap, { TradeProps } from '../../../trade/swap/swap';
import { TradeContextProvider } from '../../../trade/trade-context';
import { useAmm } from '../../amm-context';
import { Asset } from '../../../asset/asset-types';
import { TokensSwapContext, TokensSwapContextProvider } from '../tokens-swap-context';

interface TokensSwapSectionProps extends Omit<TradeProps, 'title' | 'actionLabel'> {
    asset: Asset;
}

const TokensSwapSection: React.FC<TokensSwapSectionProps> = ({ asset, ...props }) => {
    const { ammState } = useAmm();
    const { isTablet } = useWindowSize();
    const [ isBuy, setIsBuy ] = useState(true);

    const inverseAssets = useMemo(
        () => filterNonEmptyValues((ammState.pools || [])
            .filter((pool) => pool.assets.some((poolAsset) => isCoinsEquals(asset, poolAsset, true)))
            .sort((pool1, pool2) => (pool2.liquidity?.value.value || 0) - (pool1.liquidity?.value.value || 0))
            .map((pool) => pool.assets.find((poolAsset) => !isCoinsEquals(asset, poolAsset, true)))),
        [ ammState.pools, asset ],
    );

    const initialAsset1 = useMemo(() => isBuy ? inverseAssets[0] : asset, [ asset, inverseAssets, isBuy ]);

    const initialAsset2 = useMemo(() => isBuy ? asset : inverseAssets[0], [ asset, inverseAssets, isBuy ]);

    const asset1AvailableBalances = useMemo(() => isBuy ? undefined : [ asset ], [ asset, isBuy ]);

    const asset2AvailableBalances = useMemo(() => isBuy ? [ asset ] : undefined, [ asset, isBuy ]);

    return (
        <TokensSwapContextProvider
            initialAsset1={initialAsset1}
            initialAsset2={initialAsset2}
            optionalAssets1={asset1AvailableBalances}
            optionalAssets2={asset2AvailableBalances}
        >
            <TokensSwapContext.Consumer>
                {(context) => (
                    <TradeContextProvider {...context} slippageToleranceMode='alert'>
                        {!isTablet && <ClaimIro asset={asset} />}
                        <Swap
                            simpleMode
                            asset1AvailableBalances={asset1AvailableBalances}
                            asset2AvailableBalances={asset2AvailableBalances}
                            onSwitchTokens={() => setIsBuy((value) => !value)}
                            {...props}
                        />
                    </TradeContextProvider>
                )}
            </TokensSwapContext.Consumer>
        </TokensSwapContextProvider>
    );
};

export default TokensSwapSection;
